import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Form,
  Space,
  InputNumber,
  Image,
  Input,
  Card,
  Tag,
  message
  // DatePicker
} from 'antd';
import styles from '@/common/Layout/index.module.less';
import FormItem from '@/common/FormItem';
import 'react-quill/dist/quill.snow.css';
import CustomButton from '@/common/CustomButton';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAllFilteredMaterials,
  createDispensing,
  getAllproducts,
  getDispenseMaterialDetails,
  ApproveDispensing
} from '../../redux/slice';
import CustomTable from '@/common/Content/CustomTable';
import { history } from 'app/history';
import { useParams } from 'react-router-dom';
import truck from '@/features/qa/components/images/truck-delivery-done.gif';
import { getLoggedInUser, getRoles } from 'utilities/helpers';
import Layout from '@/common/Layout';

const { TextArea } = Input;

// Lifecycle colours for the status chip in the card header.
const STATUS_COLORS = {
  'QA Pending': 'orange',
  'WH LC Pending': 'blue',
  'QA LC Pending': 'blue',
  Approved: 'green',
  Dispensed: 'green'
};

const DispensingView = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const params = useParams();
  const { user } = getLoggedInUser();
  const { Superadmindata } = getRoles('Quality Control');
  // The route renders this component without props, so the old `loading` prop was
  // always undefined. Read the fetch flag from the store instead, and keep the
  // Approve button on its own local flag — getMaterialDetailslLoading is shared by
  // ~15 thunks in this slice and would otherwise spin the button on first paint.
  const { getMaterialDetailslLoading } = useSelector(
    (state) => state.dispensing
  );
  const [submitting, setSubmitting] = useState(false);

  const [value, setValue] = useState('');
  const [type, setType] = useState('');
  const [data, setData] = useState([]);
  const [product, setProduct] = useState('');
  const [truckShow, setTruckShow] = useState(false);
  const [creationComment, setCreationComment] = useState('');
  const [drStatus, setDrStatus] = useState('');
  const [requestNumber, setRequestNumber] = useState('');
  // Client-side pagination: the detail fetch returns every dispensing_material in
  // one response, so there is no paged endpoint and useServerPagination (which is
  // for API-paged lists) does not apply. Same shape as updateBinPage — slice
  // locally, report the full length as `total`.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pagedData = (data || []).slice((page - 1) * pageSize, page * pageSize);

  // A refetch replaces the row set, so start back at page 1. Keyed on the row
  // count, not the array itself: onChangeWeight replaces `data` on every
  // keystroke, which would otherwise snap the editor back to page 1 mid-edit.
  useEffect(() => {
    setPage(1);
  }, [(data || []).length]);

  // Only the QA approver (the role the Approve button is rendered for) has to
  // leave a comment. The qa_initiator "Update" path below shares this form and
  // must stay unaffected.
  const isApprover = params.id && user?.role == 'qc_initiator';
  // A material indent only awaits QA while it sits at 'QA Pending'. Once it has
  // moved on (WH LC Pending -> QA LC Pending -> Approved -> Dispensed) the QA
  // sign-off is a completed record: show it read-only and block re-approval, which
  // would otherwise overwrite the stored comment and drag the status backwards.
  const isApproved = !!drStatus && drStatus !== 'QA Pending';

  useEffect(() => {
    handleGetAllMaterialsList();
    handleGetAllProductsList();
  }, []);
  useEffect(() => {
    handleGetAllMaterials();
  }, []);
  const handleGetAllMaterials = (type) => {
    let payload = {
      id: params.id
    };
    dispatch(getDispenseMaterialDetails(payload)).then(function (result) {
      if (result?.payload?.success) {
        setCreationComment(result?.payload?.data?.creation_comment || '');
        setDrStatus(result?.payload?.data?.status || '');
        // Material requests carry 'Material-Indent-XXXX' here, BMR requests the
        // BMR/BPR code — either way it identifies the record in the header.
        setRequestNumber(result?.payload?.data?.bmr_number || '');
        // Render the QA comment already on record so a revisit shows what was
        // signed off rather than an empty box.
        form.setFieldsValue({
          qa_comment: result?.payload?.data?.qa_comment || ''
        });
        if (result?.payload?.data?.type == 'bmr') {
          //   setDispenseData(result?.payload?.data);
          setProduct([result?.payload?.data?.product_detail]);
          setValue(result?.payload?.data?.bmr_partial_details);
          setType({
            ...type,
            ['type']: result?.payload?.data?.type,
            ['sub_type']:
              result?.payload?.data?.is_partial == 'false' ? 'full' : 'partial'
          });
          form.setFieldsValue({
            batch_number: result?.payload?.data?.product_batch_number
          });
        } else {
          setType({
            ...type,
            ['type']: result?.payload?.data?.type,
            ['sub_type']:
              result?.payload?.data?.is_partial == 'false' ? 'full' : 'partial'
          });
          const dis_materials = [];
          result?.payload?.data?.dispensing_materials?.map((item) => {
            dis_materials.push({
              batch_id: item?.batch_details_id,
              required_weight: item?.required_weight,
              material_id: item?.material_id,
              material_name: item?.material?.name,
              material_code: item?.material?.code,
              batch_number: item?.batch_details?.inhouse_batch_number,
              dispensing_material_id: item?.id
            });
          });
          setData(dis_materials);
        }
      }
    });
  };

  const handleGetAllMaterialsList = () => {
    let payload = {};
    dispatch(getAllFilteredMaterials(payload));
  };

  const handleGetAllProductsList = () => {
    let payload = {};
    dispatch(getAllproducts(payload));
  };

  const handleApprove = () => {
    // Approve sits outside the form's submit handler, so the QA comment rule has
    // to be run explicitly before the request goes out.
    form
      .validateFields(['qa_comment'])
      .then((values) => {
        const payload = {
          dispensing_request_id: params.id,
          status: 'WH LC Pending',
          qa_comment: values?.qa_comment
        };
        setSubmitting(true);
        dispatch(ApproveDispensing(payload)).then((response) => {
          setSubmitting(false);
          if (response?.payload?.success) {
            // The slice only surfaces failures; success toasts are raised by the
            // component here, matching DispensingPage.
            message.success(response?.payload?.message);
            history.push('/dispense-qa-materials-list');
          }
        });
      })
      .catch(() => {});
  };
  const onFinish = (values) => {
    setTruckShow(true);
    const payload = {
      type: user?.role == 'qa_initiator' ? 'material' : type?.type,
      is_partial: type?.sub_type == 'partial' ? true : false,
      sub_type: type?.sub_type,
      product_batch_number: values?.batch_number,
      effective_date: product[0]?.effective_date,
      batch_size: product[0]?.theoretical_batch_size,
      dispensing_request_id: params.id,
      ref_mfc: product[0]?.ref_mfc,
      shelf_life: product[0]?.shelf_life,
      manufacturing_site: product[0]?.manufacturing_site,
      customer_details: product[0]?.customer_details,
      manufacturing_date: values.manufacturing_date,
      manufacturing_start_date: values.manufacturing_start_date,
      expiry_date: values.expiry_date,
      manufacturing_end_date: values.manufacturing_end_date,
      product_id: values.product_id,
      bmr_partial_details: value,
      material_list: data,
      bmr_number: product[0]?.bmr_bpr_code
    };
    dispatch(createDispensing(payload)).then(function (response) {
      if (response?.payload?.success) {
        setTruckShow(false);
        type?.type == 'bmr' && type?.sub_type == 'auto'
          ? history.push(
              `/product-details/${response?.payload?.data?.product_id}`
            )
          : user?.role == 'qa_initiator'
          ? history.push('/dispense-qa-materials-list')
          : history.push('/dispense-qa-materials-list');
      } else {
        setTruckShow(false);
      }
    });
  };
  const onChangeWeight = (value, index) => {
    setData((prevData) => {
      const newData = [...prevData];
      const newItem = { ...newData[index] };
      newItem.required_weight = value;
      newData[index] = newItem;

      return newData;
    });
  };

  const columns = [
    {
      title: 'Material Code',
      dataIndex: 'material_code',
      key: 'material_code',
      width: '150px',
      ellipsis: true,
      sorter: (a, b) => a?.material_code?.localeCompare(b?.material_code),
      responsive: ['xl', 'xs', 'sm', 'xxl', 'md']
    },
    {
      title: 'Material Name',
      dataIndex: 'material_name',
      key: 'material_name',
      width: '150px',
      ellipsis: true,
      sorter: (a, b) => a?.material_name?.localeCompare(b?.material_name),
      responsive: ['xl', 'xs', 'sm', 'xxl', 'md']
    },
    {
      title: 'Required Weight(UOM)',
      dataIndex: 'required_weight',
      key: 'required_weight',
      width: '150px',
      ellipsis: true,
      sorter: (a, b) => a?.required_weight?.localeCompare(b?.required_weight),
      // Editable only for the roles that can actually submit it. The approver's
      // Approve button posts just the id/status/comment and never runs onFinish,
      // so for them this input silently discarded whatever was typed. The
      // qa_initiator L3 / QA super user "Update" path does send material_list, so
      // it keeps the editable field (and its material_indent_changelog trail).
      render: (text, _, index) => {
        // `index` is relative to the current page slice, but the edit writes into
        // the full `data` array — offset it or page 2 would overwrite page 1's rows.
        const rowIndex = (page - 1) * pageSize + index;
        return params.id && !isApprover ? (
          <InputNumber
            value={data[rowIndex]?.required_weight}
            min={0}
            style={{
              width: '100%'
            }}
            placeholder="Enter Required Weight"
            onChange={(v) => onChangeWeight(v, rowIndex)}
          />
        ) : (
          text
        );
      },
      responsive: ['xl', 'xs', 'sm', 'xxl', 'md']
    }
  ];
  return (
    <Layout sider={false}>
      <Card
        title={`Dispensing Request${
          requestNumber ? ` - ${requestNumber}` : ''
        }`}
        className={styles.CustomPanel}
        extra={
          drStatus ? (
            <Tag color={STATUS_COLORS[drStatus] || 'default'}>{drStatus}</Tag>
          ) : null
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          // onFinishFailed={onFinishFailed}
        >
          <div className={`${styles.table_container}`}>
            <CustomTable
              columns={columns}
              dataSource={pagedData}
              loading={getMaterialDetailslLoading}
              scroll={{ x: 'auto' }}
              showPaginationMeta
              pagination={{
                size: 'small',
                total: (data || []).length,
                current: page,
                pageSize: pageSize,
                onChange: (newPage, newSize) => {
                  if (newSize !== pageSize) {
                    setPageSize(newSize);
                    setPage(1);
                  } else {
                    setPage(newPage);
                  }
                }
              }}
            />
          </div>
          <Row gutter={24} style={{ marginTop: 24 }}>
            <Col xs={24} xl={12} span={12}>
              <FormItem label="Creation Comment" className={styles.customLable}>
                <TextArea
                  rows={3}
                  value={creationComment}
                  placeholder="No creation comment was recorded for this request"
                  disabled
                />
              </FormItem>
            </Col>
            <Col xs={24} xl={12} span={12}>
              <FormItem
                label="QA Comment"
                name="qa_comment"
                rules={[
                  {
                    required: isApprover && !isApproved,
                    whitespace: true,
                    message: 'Enter QA comment'
                  }
                ]}
                className={styles.customLable}
              >
                <TextArea
                  rows={3}
                  placeholder={
                    isApproved
                      ? 'No QA comment was recorded for this request'
                      : 'Enter QA comment'
                  }
                  disabled={isApproved}
                />
              </FormItem>
            </Col>
          </Row>
          <FormItem className="text-center">
            <Space>
              <CustomButton type="primary" ghost onClick={() => history.back()}>
                Back
              </CustomButton>
              {truckShow ? (
                <Image
                  src={truck}
                  preview={false}
                  style={{ width: '100px !important', height: '100px' }}
                />
              ) : isApprover ? (
                <CustomButton
                  type="primary"
                  onClick={() => handleApprove()}
                  loading={submitting}
                  disabled={isApproved || submitting}
                >
                  {'Approve'}
                </CustomButton>
              ) : (
                <CustomButton type="primary" htmlType="submit">
                  {(params.id &&
                    user?.role == 'qa_initiator' &&
                    user?.level == 3) ||
                  Superadmindata?.[0]?.level == 3
                    ? 'Update'
                    : type?.type == 'bmr' && type?.sub_type == 'auto'
                    ? 'Next'
                    : 'Submit'}
                </CustomButton>
              )}
            </Space>
          </FormItem>
        </Form>
      </Card>
    </Layout>
  );
};
export default DispensingView;
